import { useEffect } from 'react'
import { Outlet, redirect, createFileRoute } from '@tanstack/react-router'
import { DRAFT_PROJECT_ID, useAgentStore } from '@/stores/agent.store'
import {
  DEFAULT_VISIBLE_AGENT_ID,
  isSyntheticAgentId,
  visibleAgentIdFor,
} from '@/features/agents/visibility'
import {
  SAMPLE_AGENT_ID,
  SAMPLE_PROJECT_ENTERED_KEY,
  isSampleProject,
} from '@/lib/sample-project'
import { PDP_AGENT_ID, useAgentRegistry } from '@/api/queries/agents'
import { BRU_AGENT_ID, RADAR_AGENT_ID, RECIPES_AGENT_ID, REMIX_AGENT_ID } from '@/api/agent-ids'
import { InitialAgentBrandModal } from '@/features/agent-entry/components/InitialAgentBrandModal'
import { BruSampleWalkthroughMount } from '@/features/canvas/plugins/bru-outputs/BruSampleWalkthroughMount'
import { DirectEntryProjectGate } from '@/features/agent-entry/components/DirectEntryProjectGate'
import { isDirectEntryAgent } from '@/features/agent-entry/direct-entry'
import { isInsightsSpaceEnabled } from '@/features/insights-space/flag'
import { draftEntryAgentFor, lensRouteGuard } from '@/features/insights-space/landing'
import { VideoGenStageSchema, type VideoGenStage } from '@/api/validation/video-gen'
import { useResetWorkspaceChrome } from '@/features/canvas/hooks/useResetWorkspaceChrome'
import { useResetSampleWalkthroughOnEntry } from '@/features/coachmarks/sample-walkthrough-reset'
import { isLegacyImageStudioDocumentPath } from '@/features/canvas/plugins/image-studio/imageStudioLegacyRoute'
import { readPlanWorkspaceLocation, type PlanWorkspaceLocation } from '@/features/plan-workspace/location'

/**
 * Search params schema for workspace views.
 *
 * Canvas navigation uses `tab`. Active Tasks may additionally provide `run`
 * to scroll the chat to a processing-run banner. Unknown params are silently
 * dropped by TanStack Router.
 */
type WorkspaceSearch = PlanWorkspaceLocation & {
  /** Opens the project invite flow after a contextual deep link. */
  invite?: boolean
  tab?: string
  run?: string
  detail?: string
  /** Exact parent-static snapshot selected within `detail`. */
  detailVersion?: number
  /** Selected Campaign Big Idea on the overview and Brief. */
  idea?: string
  /** Quick Ads image adaptation selected inside `detail`. Animated statics use
   *  `section`; the published content type still lives in canvas state (§7a). */
  nested?: string
  /** Exact historical version of the selected Quick Ads adaptation. */
  nestedVersion?: number
  /**
   * Image Studio document open as a takeover of this public source-agent route.
   *
   * The editor is a STATE of the workspace route rather than a route of its
   * own, so source route context stays addressable beside it and returning is
   * dropping this one param. See `imageStudioEditorLocation`.
   */
  editor?: string
  /** Pinned version of `editor`'s document; meaningless without it. */
  version?: number
  segment?: string
  v?: number
  section?: string
  /** PDP: the marketplace channel whose adaptation is shown (absent = the Amazon source). */
  channel?: string
  /** PDP: exact historical version of `channel`'s adaptation (absent = latest). */
  channelV?: number
  from?: string
  /**
   * Brand Playbook opened from another agent's chat: that agent's own playbook
   * selection is previewed read-only. Absent on direct entry (normal states).
   */
  playbookFrom?: string
  /** Media gallery lightbox: slot position currently displayed. */
  slot?: number
  /** Media gallery lightbox: side currently displayed (`optimized` | `original`). */
  side?: 'optimized' | 'original'
  /** Explicit read-only conversation surface; never an active thread target. */
  history?: 'archives' | 'imported'
  archiveType?: string
  archiveId?: string
  /** Suppresses PDP's automatic tab restore after a typed content 410. */
  contentRemoved?: boolean
  /** Video Gen-only historical stage projection. */
  'vg-stage'?: VideoGenStage
  /** Video Gen-only: the embedded Video Studio step after Storyboard. */
  'vg-studio'?: true
  /** Quick Ads-only: open Ad Detail (`detail`) on its Short Video tab. */
  'qa-tab'?: 'short-video'
  /** Content Strategy's namespaced, shareable workspace selection. */
  'cs-content'?: string
  'cs-version'?: number
  'cs-stage'?: 'overview' | 'audit' | 'strategy' | 'calendar'
  'cs-audit'?: 'health' | 'competition' | 'trending'
  'cs-item-kind'?: 'rival' | 'trend' | 'territory' | 'slot'
  'cs-item'?: string
  'cs-detail'?: 'slot'
  'cs-rival-view'?: 'performance' | 'evidence' | 'audience' | 'pillars'
  'cs-platform'?: string
  'cs-market-view'?: 'attack' | 'opportunities' | 'edge'
  'cs-health-view'?: 'trends' | 'content_activity'
  'cs-health-lane'?: 'working' | 'underperforming'
  'cs-health-month'?: string
}

function nonBlankSearchString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  return value.trim() ? value : undefined
}

/** An agent id is a lowercase identifier; anything else is not a preview source. */
function validPlaybookSourceAgent(value: unknown): string | undefined {
  return typeof value === 'string' && /^[a-z][a-z0-9_]{0,63}$/.test(value) ? value : undefined
}

/** Rehydrate workspace URL state without accepting an orphaned nested target. */
export function validateWorkspaceSearch(
  search: Record<string, unknown>,
): WorkspaceSearch {
  const detail = nonBlankSearchString(search.detail)
  // The editor takeover survives a reload on its own: an Ad Detail that failed
  // to restore is a degraded trail, not a reason to swallow the document the
  // shared link was for.
  const editor = nonBlankSearchString(search.editor)
  const history = search.history === 'archives' || search.history === 'imported'
    ? search.history
    : undefined
  const archiveType = history === 'archives'
    ? nonBlankSearchString(search.archiveType)
    : undefined
  const archiveId = history === 'archives' && archiveType
    ? nonBlankSearchString(search.archiveId)
    : undefined
  const videoGenStage = VideoGenStageSchema.safeParse(search['vg-stage'])
  const csStage =
    search['cs-stage'] === 'overview' ||
    search['cs-stage'] === 'audit' ||
    search['cs-stage'] === 'strategy' ||
    search['cs-stage'] === 'calendar'
      ? search['cs-stage']
      : undefined
  const csAudit =
    search['cs-audit'] === 'health' ||
    search['cs-audit'] === 'competition' ||
    search['cs-audit'] === 'trending'
      ? search['cs-audit']
      : undefined
  const csItemKind =
    search['cs-item-kind'] === 'rival' ||
    search['cs-item-kind'] === 'trend' ||
    search['cs-item-kind'] === 'territory' ||
    search['cs-item-kind'] === 'slot'
      ? search['cs-item-kind']
      : undefined
  const csItem = nonBlankSearchString(search['cs-item'])
  const csRivalView =
    search['cs-rival-view'] === 'performance' ||
    search['cs-rival-view'] === 'evidence' ||
    search['cs-rival-view'] === 'audience' ||
    search['cs-rival-view'] === 'pillars'
      ? search['cs-rival-view']
      : undefined
  const csMarketView =
    search['cs-market-view'] === 'attack' ||
    search['cs-market-view'] === 'opportunities' ||
    search['cs-market-view'] === 'edge'
       ? search['cs-market-view']
       : undefined
  return {
    ...readPlanWorkspaceLocation(search),
    invite: search.invite === true || search.invite === 'true' ? true : undefined,
    tab: typeof search.tab === 'string' ? search.tab : undefined,
    run: typeof search.run === 'string' ? search.run : undefined,
    detail,
    detailVersion:
      detail
      && !nonBlankSearchString(search.nested)
      && typeof search.detailVersion === 'number'
      && Number.isInteger(search.detailVersion)
      && search.detailVersion > 0
        ? search.detailVersion
        : undefined,
    idea: nonBlankSearchString(search.idea),
    nested: detail ? nonBlankSearchString(search.nested) : undefined,
    nestedVersion:
      detail
      && nonBlankSearchString(search.nested)
      && typeof search.nestedVersion === 'number'
      && Number.isInteger(search.nestedVersion)
      && search.nestedVersion > 0
        ? search.nestedVersion
        : undefined,
    editor,
    playbookFrom: validPlaybookSourceAgent(search.playbookFrom),
    version:
      editor
      && typeof search.version === 'number'
      && Number.isInteger(search.version)
      && search.version > 0
        ? search.version
        : undefined,
    segment: typeof search.segment === 'string' ? search.segment : undefined,
    v: typeof search.v === 'number' ? search.v : undefined,
    section: typeof search.section === 'string' ? search.section : undefined,
    channel: nonBlankSearchString(search.channel),
    channelV:
      nonBlankSearchString(search.channel)
      && typeof search.channelV === 'number'
      && Number.isInteger(search.channelV)
      && search.channelV > 0
        ? search.channelV
        : undefined,
    from: typeof search.from === 'string' ? search.from : undefined,
    slot: typeof search.slot === 'number' ? search.slot : undefined,
    side:
      search.side === 'optimized' || search.side === 'original'
        ? search.side
        : undefined,
    history,
    archiveType: archiveId ? archiveType : undefined,
    archiveId,
    contentRemoved:
      search.contentRemoved === true || search.contentRemoved === 'true'
        ? true
        : undefined,
    'vg-stage': videoGenStage.success ? videoGenStage.data : undefined,
    'vg-studio': search['vg-studio'] === true || search['vg-studio'] === 'true' ? true : undefined,
    'qa-tab': search['qa-tab'] === 'short-video' ? 'short-video' : undefined,
    'cs-content': nonBlankSearchString(search['cs-content']),
    'cs-version':
      typeof search['cs-version'] === 'number' &&
      Number.isInteger(search['cs-version']) &&
      search['cs-version'] > 0
        ? search['cs-version']
        : undefined,
    'cs-stage': csStage,
    'cs-audit': csStage === 'audit' ? csAudit : undefined,
    'cs-item-kind': csItem ? csItemKind : undefined,
    'cs-item': csItemKind ? csItem : undefined,
    'cs-detail':
      csStage === 'calendar' && csItemKind === 'slot' && csItem && search['cs-detail'] === 'slot'
        ? 'slot'
        : undefined,
    'cs-rival-view': csStage === 'audit' && csAudit === 'competition' && csItemKind === 'rival' && csItem
      ? csRivalView
      : undefined,
    'cs-platform': csStage === 'audit' && (
      csAudit === 'health' || (csAudit === 'competition' && csItemKind === 'rival' && csItem)
    )
      ? nonBlankSearchString(search['cs-platform'])
      : undefined,
    'cs-market-view': csStage === 'audit' && csAudit === 'competition'
      ? csMarketView
      : undefined,
    'cs-health-view': csStage === 'audit' && csAudit === 'health' && (search['cs-health-view'] === 'content_activity' || search['cs-health-view'] === 'trends') ? search['cs-health-view'] : undefined,
    'cs-health-lane': csStage === 'audit' && csAudit === 'health' && (search['cs-health-lane'] === 'underperforming' || search['cs-health-lane'] === 'working') ? search['cs-health-lane'] : undefined,
    'cs-health-month': csStage === 'audit' && csAudit === 'health' && typeof search['cs-health-month'] === 'string' && /^\d{4}-\d{2}$/u.test(search['cs-health-month']) ? search['cs-health-month'] : undefined,
  }
}

/** Session-storage sentinel: sample-project entry redirect fires at most once
 *  per browser session. Any first arrival to the sample project (URL, table
 *  row, session restore) bounces to Quick Ads Inputs; subsequent navigation
 *  within the sample honours the URL so free-roam works after the tour lands. */
function shouldRedirectSampleEntry(projectId: string, agentId: string): boolean {
  if (!isSampleProject(projectId)) return false
  if (agentId === SAMPLE_AGENT_ID) return false
  // `sessionStorage` is unavailable during SSR / test contexts without a DOM.
  if (typeof window === 'undefined') return false
  return window.sessionStorage.getItem(SAMPLE_PROJECT_ENTERED_KEY) === null
}

/**
 * `/<pid>/insights` is the Insights space's address on its own build; this
 * build has no such agent, and the link used to load forever. It opens the
 * lens the link points at instead, on the same scan or report.
 */
/**
 * Where an `/insights` link goes: with the Insights space off, the lens it names (a bookmark or an
 * older build's link); with it on, nowhere, because `/insights` is the space. Redirecting it there as
 * well sent the space back to a lens whose route `lensRouteGuard` sends to the space, forever.
 */
export function insightsLinkRedirect(
  search: Record<string, unknown>,
  insightsSpace: boolean = isInsightsSpaceEnabled(),
): { agentId: string, tab?: string } | null {
  return insightsSpace ? null : insightsLinkTarget(search)
}

export function insightsLinkTarget(search: Record<string, unknown>): { agentId: string, tab?: string } {
  const section = typeof search.section === 'string' ? search.section : ''
  const tab = typeof search.tab === 'string' && search.tab.trim() ? search.tab : undefined
  const agentId = section.startsWith('radar-') || tab?.startsWith('radar_')
    ? RADAR_AGENT_ID
    : section.startsWith('recipe-') ? RECIPES_AGENT_ID : REMIX_AGENT_ID
  return tab ? { agentId, tab } : { agentId }
}

export const Route = createFileRoute('/_app/$projectId/$agentId')({
  beforeLoad: ({ params, location, search }) => {
    if (shouldRedirectSampleEntry(params.projectId, params.agentId)) {
      window.sessionStorage.setItem(SAMPLE_PROJECT_ENTERED_KEY, '1')
      throw redirect({
        to: '/$projectId/$agentId',
        params: { projectId: params.projectId, agentId: SAMPLE_AGENT_ID },
        search: { section: 'inputs' },
        replace: true,
      })
    }
    // Mark session as entered on any first arrival to the sample project, even
    // when we did not need to redirect — so a subsequent `/sample/bru` URL is
    // honoured as free-roam and not bounced back to Quick Ads.
    if (
      isSampleProject(params.projectId) &&
      typeof window !== 'undefined' &&
      window.sessionStorage.getItem(SAMPLE_PROJECT_ENTERED_KEY) === null
    ) {
      window.sessionStorage.setItem(SAMPLE_PROJECT_ENTERED_KEY, '1')
    }
    // Pre-takeover document links are the sole hidden-agent exception. The
    // child reads dedicated document provenance and immediately redirects into
    // a public source-agent workspace; `/image_studio` itself still normalizes.
    const insightsTarget = params.agentId === 'insights'
      ? insightsLinkRedirect(location.search as Record<string, unknown>)
      : null
    if (insightsTarget) {
      throw redirect({
        to: '/$projectId/$agentId',
        params: { projectId: params.projectId, agentId: insightsTarget.agentId },
        search: insightsTarget.tab ? { tab: insightsTarget.tab } : {},
        replace: true,
      })
    }
    if (isLegacyImageStudioDocumentPath(params.agentId, location.pathname)) return
    // `/new/insights` has no agent to make a project on; it starts on the
    // space's default lens, whose new project then opens in the space.
    if (params.projectId === DRAFT_PROJECT_ID && draftEntryAgentFor(params.agentId) !== params.agentId) {
      throw redirect({
        to: '/$projectId/$agentId',
        params: { projectId: DRAFT_PROJECT_ID, agentId: draftEntryAgentFor(params.agentId) },
        replace: true,
      })
    }
    // With the Insights space on, a lens's own route opens the space on that
    // lens, replacing the lens URL in history (Back included).
    lensRouteGuard(params, search)
    const visibleAgentId = visibleAgentIdFor(params.agentId)
    if (visibleAgentId !== params.agentId) {
      throw redirect({
        to: '/$projectId/$agentId',
        params: { projectId: params.projectId, agentId: DEFAULT_VISIBLE_AGENT_ID },
        replace: true,
      })
    }
  },
  validateSearch: validateWorkspaceSearch,
  component: AgentLayout,
})

function AgentLayout() {
  const { projectId, agentId } = Route.useParams()
  const { data: agentRegistry } = useAgentRegistry()
  // Replay the sample walkthrough on every fresh entry into the sample project.
  // See sample-walkthrough-reset.ts for the sentinel policy.
  useResetSampleWalkthroughOnEntry(projectId)
  const isPdpFlow =
    agentId === PDP_AGENT_ID ||
    agentRegistry?.find((agent) => agent.agent_id === agentId)?.output_panel ===
      'pdp_panel'
  const isBrandExempt = isPdpFlow || isSyntheticAgentId(agentId)
  useResetWorkspaceChrome(projectId, agentId)

  // Sync route param → store in an effect (not render-phase) to avoid
  // cascading renders / StrictMode double-invoke surprises. Consumers that
  // need the agentId synchronously on first render should read it from
  // route params at their call site (as Shell/ChatPanel do).
  useEffect(() => {
    if (projectId === DRAFT_PROJECT_ID) {
      useAgentStore.getState().enterDraftProject(agentId)
      return
    }
    useAgentStore.getState().setActiveAgent(agentId)
  }, [agentId, projectId])

  if (projectId === DRAFT_PROJECT_ID && isDirectEntryAgent(agentId)) {
    return <DirectEntryProjectGate agentId={agentId} />
  }

  return (
    <>
      <Outlet />
      {projectId === DRAFT_PROJECT_ID && !isBrandExempt && (
        <InitialAgentBrandModal
          key={`${projectId}:${agentId}`}
          agentId={agentId}
        />
      )}
      {/*
        Bru's sample-project walkthrough mount lives at the route level because
        its canvas plugin only mounts when the project has saved outputs — but
        the chain-hop coachmark from Quick Ads must auto-fire on arrival whether
        the outputs rail is visible or not. See BruSampleWalkthroughMount.tsx.
      */}
      {agentId === BRU_AGENT_ID && isSampleProject(projectId) && (
        <BruSampleWalkthroughMount />
      )}
    </>
  )
}
